import { useRef, useState } from "react";
import { UploadCloud, FileAudio, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtBytes } from "@/lib/format";

export const UploadDropzone = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [project, setProject] = useState("");
  const [episode, setEpisode] = useState("");

  const upload = async (file) => {
    const id = `${file.name}-${Date.now()}`;
    setUploads((u) => [...u, { id, name: file.name, size: file.size, pct: 0, stage: "uploading" }]);
    const form = new FormData();
    form.append("file", file);
    form.append("project_id", project);
    form.append("episode_id", episode);
    try {
      await api.post("/media/upload", form, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, pct, stage: pct >= 100 ? "analysing audio…" : "uploading" } : x)));
        },
      });
      toast.success(`${file.name} ingested`);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Upload failed: ${file.name}`);
    } finally {
      setUploads((u) => u.filter((x) => x.id !== id));
    }
  };

  const handleFiles = (files) => Array.from(files || []).forEach(upload);

  return (
    <div className="panel p-4">
      <div className="flex flex-wrap gap-3 mb-3">
        <label className="flex flex-col gap-1">
          <span className="micro">Project ID</span>
          <input data-testid="upload-project-id-input" value={project} onChange={(e) => setProject(e.target.value)} placeholder="e.g. SHOW-01"
            className="bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-sm font-mono w-40 focus:outline-none focus:border-cyan-500" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="micro">Episode ID</span>
          <input data-testid="upload-episode-id-input" value={episode} onChange={(e) => setEpisode(e.target.value)} placeholder="e.g. EP07"
            className="bg-[#0B0E14] border border-[#253044] rounded-sm px-2.5 py-1.5 text-sm font-mono w-32 focus:outline-none focus:border-cyan-500" />
        </label>
      </div>
      <div data-testid="upload-master-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border border-dashed rounded-md px-6 py-8 text-center transition-colors ${drag ? "border-cyan-400 bg-cyan-400/5" : "border-[#3A4A68] hover:border-slate-400 hover:bg-[#1A2130]"}`}>
        <UploadCloud className="mx-auto mb-2 text-slate-400" size={26} />
        <div className="text-sm text-slate-200 font-medium">Drop finished masters here or click to browse</div>
        <div className="text-xs text-slate-500 mt-1">WAV · FLAC · AIFF · MP4 · MOV · MXF · MKV — audio is extracted losslessly, PCM WAV masters are used untouched</div>
        <input ref={inputRef} data-testid="upload-file-input" type="file" multiple className="hidden"
          accept=".wav,.flac,.aif,.aiff,.mp4,.mov,.mxf,.mkv,.mp3,.m4a,audio/*,video/*" onChange={(e) => handleFiles(e.target.files)} />
      </div>
      {uploads.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploads.map((u) => (
            <div key={u.id} data-testid="upload-progress-row" className="flex items-center gap-3 text-xs">
              <FileAudio size={14} className="text-slate-400" />
              <span className="font-mono text-slate-300 truncate flex-1">{u.name}</span>
              <span className="text-slate-500">{fmtBytes(u.size)}</span>
              <div className="w-40 h-1.5 bg-[#253044] rounded-full overflow-hidden">
                <div className="h-full bg-cyan-400 transition-[width]" style={{ width: `${u.pct}%` }} />
              </div>
              <span className="text-slate-400 w-28 flex items-center gap-1"><Loader2 size={11} className="animate-spin" />{u.stage}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
